import { TaskMobileForm } from "./TaskMobileForm.tsx";
import { SingleTaskMobileViewFooter } from "src/components/views/tasks/mobile/SingleTaskMobileViewFooter.tsx";
import { VerticalBox } from "../../../common/box/VerticalBox.tsx";
import { useEffect, useState } from "react";
import { TaskView } from "src/api/generated/postgres/db/types/tasks/tables/taskView.ts";
import { TaskUpdateApi } from "src/api/generated/tasks/api/taskUpdateApi.ts";
import { TaskCreationApi } from "src/api/generated/tasks/api/taskCreationApi.ts";
import { LoadingBox } from "src/components/common/LoadingBox.tsx";
import { Divider } from "@mui/material";
import { TasksViewProps } from "src/components/views/tasks/TasksView.tsx";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import dayjs from "dayjs";
import { TaskWithUserNames } from "src/components/views/tasks/types.ts";
import { dayJsToDateIsoString } from "src/utils/dayjsUtils.ts";
import { taskIsSaved } from "../taskUtils.ts";
import { TaskSourceRef } from "src/api/generated/tasks/taskSource/taskSourceRef.ts";
import { useAavoForm } from "src/components/common/forms/useAavoForm/useAavoForm.ts";
import { DataDirtyStateChangeHandler } from "src/utils/dataDirtyStateChangeHandler.ts";

export interface SingleTaskMobileViewProps
	extends Pick<TasksViewProps, "onTaskCompleted" | "onTaskCompletionReverted"> {
	task: TaskView | null;
	refreshTask?: () => Promise<TaskView | undefined>;
	closeTaskView: () => void;
	refreshTasks: () => Promise<unknown>;
	newTaskSourceRef?: TaskSourceRef;
	onDataDirtyStateChanged?: DataDirtyStateChangeHandler;
}

export const SingleTaskMobileView = ({
	task: taskInitial,
	refreshTask: refreshTaskProp,
	onTaskCompleted,
	onTaskCompletionReverted,
	closeTaskView,
	refreshTasks,
	newTaskSourceRef,
	onDataDirtyStateChanged,
}: SingleTaskMobileViewProps) => {
	const { appUserId, userName } = useGlobalInitData();
	const [showOverlaySpinner, setShowOverlaySpinner] = useState(false);

	const isNewTask = taskInitial == null;
	const [isEditing, setIsEditing] = useState(isNewTask);
	const [autoFocusField, setAutoFocusField] = useState<keyof TaskWithUserNames | null>(
		isNewTask ? "title" : null,
	);

	const form = useAavoForm<TaskWithUserNames, number>({
		defaultValues: taskInitial ?? getNewTask(),
		onSubmit: async (values) => {
			if (taskIsSaved(values)) {
				await TaskUpdateApi.updateTask({ task: values });
				return values.taskId;
			} else {
				return await TaskCreationApi.createTask({ task: values });
			}
		},
		onCompleted: async (result) => {
			if (result.type === "success") {
				if (isNewTask) {
					closeTaskView();
					await refreshTasks();
				} else {
					setIsEditing(false);
					await refreshTask();
				}
			}
		},
	});

	const { isDirty } = form.formState;
	useEffect(() => {
		onDataDirtyStateChanged?.({ isDirty });
	}, [isDirty, onDataDirtyStateChanged]);

	return (
		<VerticalBox
			sx={{
				flex: 1,
			}}
		>
			<TaskMobileForm
				control={form.control}
				setValue={form.setValue}
				getValues={form.getValues}
				isEditing={isEditing}
				autoFocusField={autoFocusField}
				onStartEdit={handleStartEdit}
				onLocationSaved={async (location) => {
					const currentValues = form.getValues();
					if (taskIsSaved(currentValues)) {
						await TaskUpdateApi.setTaskLocation({
							taskId: currentValues.taskId,
							latitude: location?.latitude ?? null,
							longitude: location?.longitude ?? null,
						});
						await refreshTask({ keepDirtyValues: true });
					}
				}}
			/>
			<Divider />
			<SingleTaskMobileViewFooter
				task={form.getValues()}
				isEditing={isEditing}
				isNewTask={isNewTask}
				onSave={saveTask}
				onCancelEdit={cancelEdit}
				onStartEdit={() => handleStartEdit()}
				refreshTask={refreshTask}
				onTaskCompleted={onTaskCompleted}
				onTaskCompletionReverted={onTaskCompletionReverted}
			/>
			{showOverlaySpinner && <LoadingBox overlay />}
		</VerticalBox>
	);

	async function saveTask() {
		try {
			setShowOverlaySpinner(true);
			await form.triggerSubmit();
		} finally {
			setShowOverlaySpinner(false);
		}
	}

	function handleStartEdit(fieldName?: keyof TaskWithUserNames) {
		setIsEditing(true);
		setAutoFocusField(fieldName ?? null);
	}

	async function cancelEdit() {
		setIsEditing(false);
		form.reset();
	}

	async function refreshTask({ keepDirtyValues }: { keepDirtyValues?: boolean } = {}) {
		try {
			setShowOverlaySpinner(true);
			const newTask = await refreshTaskProp?.();
			if (newTask !== undefined) {
				form.reset(newTask, { keepDirtyValues });
			}
		} finally {
			setShowOverlaySpinner(false);
		}
	}

	function getNewTask(): TaskWithUserNames {
		return {
			taskId: -1,
			title: "",
			taskDescription: "",
			sourceDescription: "",
			earliestStartDate: null,
			deadlineDate: dayJsToDateIsoString(dayjs()),
			note: "",
			taskState: "INITIAL",
			createdAt: dayjs().toISOString(),
			createdByUserId: appUserId,
			assignedToUserId: appUserId,
			assignedToUserName: userName,
			responsiblePersonUserId: appUserId,
			responsiblePersonUserName: userName,
			canBeStarted: true,
			showInSchedulePlanning: false,
			lastUpdated: null,
			sourceType: newTaskSourceRef?.sourceType,
			sourceId: newTaskSourceRef?.sourceId,
			releasedDate: null,
			actualStartDate: null,
			completedDate: null,
			cancelledDate: null,
			workloadEstimateHours: null,
			internalContext: "",
			startedByUserId: null,
			completedByUserId: null,
			locationLatitude: null,
			locationLongitude: null,
		};
	}
};
